/**
 * WidgetWrapper - Common frame/chrome for all widgets
 *
 * Provides:
 * - Header with title and drag handle
 * - Settings button
 * - Remove button
 * - Consistent styling
 */

import React from 'react';
import { Settings, X, GripVertical } from 'lucide-react';
import { useWindows } from '../../contexts/WindowsContext';

interface WidgetWrapperProps {
  title: React.ReactNode;
  widgetId: string;
  windowId: string;
  children: React.ReactNode;
  onSettings?: () => void;
  onHeaderClick?: () => void;
  className?: string;
}

export function WidgetWrapper({
  title,
  widgetId,
  windowId,
  children,
  onSettings,
  onHeaderClick,
  className = '',
}: WidgetWrapperProps) {
  const { removeWidget, isEditing } = useWindows();

  return (
    <div
      className={`h-full flex flex-col bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden ${isEditing ? 'ring-2 ring-blue-300 dark:ring-blue-700 ring-opacity-50' : ''} ${className}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <div className="widget-drag-grip cursor-grab active:cursor-grabbing p-1.5 -ml-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 touch-manipulation">
            <GripVertical className="w-5 h-5 text-gray-400" />
          </div>
          {onHeaderClick ? (
            <button
              onClick={onHeaderClick}
              className="text-sm font-medium text-gray-700 dark:text-gray-200 truncate cursor-pointer hover:underline text-left"
            >
              {title}
            </button>
          ) : (
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200 truncate">
              {title}
            </h3>
          )}
        </div>

        {/* Controls - always visible for touch support */}
        <div className="flex items-center gap-1">
          {onSettings && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onSettings();
              }}
              className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 touch-manipulation"
              title="Widget settings"
            >
              <Settings className="w-4 h-4" />
            </button>
          )}
          {isEditing && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                removeWidget(windowId, widgetId);
              }}
              className="p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900 text-gray-500 hover:text-red-600 dark:text-gray-400 touch-manipulation"
              title="Remove widget"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto" style={{ containerType: 'size' }}>{children}</div>
    </div>
  );
}
